import { Sun, Cloud, CloudRain, CloudSnow, CloudFog, CloudLightning, Moon, CloudSun } from 'lucide-react';

const ATMOSPHERE_PRESETS = {
  clear: {
    overlay: 'bg-gradient-to-b from-amber-400/5 via-slate-900/15 to-slate-900/45',
  },
  partly: {
    overlay: 'bg-gradient-to-b from-sky-300/10 via-slate-900/20 to-slate-900/50',
  },
  cloudy: {
    overlay: 'bg-gradient-to-b from-slate-400/20 via-slate-700/30 to-slate-900/55',
  },
  fog: {
    overlay: 'bg-gradient-to-b from-slate-200/15 via-slate-400/25 to-slate-700/50',
  },
  rain: {
    overlay: 'bg-gradient-to-b from-slate-600/30 via-slate-800/40 to-slate-900/65',
  },
  snow: {
    overlay: 'bg-gradient-to-b from-sky-200/15 via-slate-500/30 to-slate-800/55',
  },
  storm: {
    overlay: 'bg-gradient-to-b from-slate-800/40 via-indigo-950/50 to-slate-950/70',
  },
  night: {
    overlay: 'bg-gradient-to-b from-indigo-950/55 via-slate-900/65 to-slate-950/80',
  },
};

const Raindrops = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
    {Array.from({ length: 40 }).map((_, i) => (
      <span
        // eslint-disable-next-line react/no-array-index-key
        key={i}
        className="absolute top-0 w-px h-12 bg-gradient-to-b from-transparent via-sky-200/40 to-transparent rain-drop"
        style={{
          left: `${(i * 2.7) % 100}%`,
          animationDelay: `${(i % 10) * 0.15}s`,
          animationDuration: `${0.7 + (i % 5) * 0.1}s`,
        }}
      />
    ))}
  </div>
);

const Snowflakes = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
    {Array.from({ length: 35 }).map((_, i) => (
      <span
        // eslint-disable-next-line react/no-array-index-key
        key={i}
        className="absolute -top-2 text-white/70 snow-flake"
        style={{
          left: `${(i * 3) % 100}%`,
          fontSize: `${10 + (i % 3) * 4}px`,
          animationDelay: `${(i % 8) * 0.6}s`,
          animationDuration: `${6 + (i % 6)}s`,
        }}
      >
        ❄
      </span>
    ))}
  </div>
);

const Stars = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
    {Array.from({ length: 60 }).map((_, i) => (
      <span
        // eslint-disable-next-line react/no-array-index-key
        key={i}
        className="absolute rounded-full bg-white star-twinkle"
        style={{
          top: `${(i * 7.3) % 60}%`,
          left: `${(i * 11.7) % 100}%`,
          width: `${1 + (i % 3)}px`,
          height: `${1 + (i % 3)}px`,
          animationDelay: `${(i % 10) * 0.4}s`,
        }}
      />
    ))}
  </div>
);

export const HeroWeatherLayer = ({ weather }) => {
  const atmosphere = weather?.atmosphere || 'clear';
  const preset = ATMOSPHERE_PRESETS[atmosphere] || ATMOSPHERE_PRESETS.clear;
  const { overlay } = preset;

  return (
    <>
      {/* Atmospheric tint overlay (replaces default dark gradient) */}
      <div className={`absolute inset-0 transition-colors duration-1000 ${overlay}`} />

      {/* Weather-specific animated effects */}
      {atmosphere === 'rain' && <Raindrops />}
      {atmosphere === 'storm' && <Raindrops />}
      {atmosphere === 'snow' && <Snowflakes />}
      {atmosphere === 'night' && <Stars />}
      {atmosphere === 'fog' && (
        <div className="absolute inset-0 backdrop-blur-[2px] bg-white/5 pointer-events-none" aria-hidden="true" />
      )}
    </>
  );
};
